import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { Link } from "@/components/LLink";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import ContactForm from "@/components/ContactForm";
import SEO from "@/components/SEO";
import { useLang, withLang } from "@/i18n";
import { COUNTRY } from "@/seo/countryContent";
import { marketFor } from "@/seo/market";

const DEDICATED = { de: ["Dedizierte Seiten", "Leistungen und Regionen im Detail"], fr: ["Pages dédiées", "Prestations et régions en détail"] };

function useFaqSchema(faq) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [faq]);
}

const H2 = "mt-6 font-heading text-3xl font-bold tracking-tight md:text-[40px]";

export default function CountryPage({ slug }) {
  const { lang } = useLang();
  const c = COUNTRY[slug];
  useFaqSchema(c.faq);
  if (lang !== c.lang) return <Navigate to={withLang(lang, "/export")} replace />;

  return (
    <div data-testid={`country-page-${slug}`}>
      <SEO title={c.seo[0]} description={c.seo[1]} image="/images/b2b.jpg" single />
      <PageHero kicker={c.kicker} title={c.title} subtitle={c.subtitle} image="/images/b2b.jpg" />

      <section className="border-b border-ink/10">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="kicker">{c.intro_k}</p>
              <Reveal><h2 className={`${H2} leading-[1.1]`} data-testid="country-intro-title">{c.intro_t}</h2></Reveal>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[17px] leading-[1.7] text-ink/70">{c.intro_p}</p>
              <Link to="/export" data-testid="country-export-link" className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-ink underline decoration-gold decoration-2 underline-offset-[6px] transition-colors hover:text-gold">
                {c.export_link} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-surface">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <p className="kicker">{c.services_k}</p>
          <h2 className={H2}>{c.services_t}</h2>
          <div className="mt-12 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="country-services">
            {c.services.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.08} className="bg-base">
                <div className="h-full p-8">
                  <span className="font-mono text-[12px] tracking-[0.25em] text-ink/40">0{i + 1}</span>
                  <h3 className="mt-6 text-[17px] font-bold tracking-tight text-ink">{title}</h3>
                  <p className="mt-3 text-[15px] leading-[1.6] text-ink/65">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="kicker">{c.regions_k}</p>
              <h2 className={H2}>{c.regions_t}</h2>
              <p className="mt-6 text-[15px] leading-[1.7] text-ink/65">{c.regions_p}</p>
            </div>
            <div className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:col-span-7" data-testid="country-regions">
              {c.regions.map((r) => (
                <div key={r} className="flex items-center gap-3 bg-base px-6 py-5 text-[15px] font-semibold text-ink">
                  <span className="h-1.5 w-1.5 bg-gold" aria-hidden /> {r}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-surface">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <p className="kicker">{c.compare_k}</p>
          <h2 className={H2}>{c.compare_t}</h2>
          <div className="mt-12 overflow-x-auto border border-ink/10 bg-base" data-testid="country-compare">
            <table className="w-full min-w-[640px] text-left text-[15px]">
              <thead>
                <tr className="border-b border-ink/10">
                  {c.compare_head.map((h, i) => (
                    <th key={i} className={`px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] ${i === 1 ? "text-gold" : "text-ink/50"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.compare.map(([k, a, b]) => (
                  <tr key={k} className="border-b border-ink/10 last:border-0">
                    <td className="px-6 py-4 font-semibold text-ink">{k}</td>
                    <td className="px-6 py-4 text-ink"><span className="inline-flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {a}</span></td>
                    <td className="px-6 py-4 text-ink/55"><span className="inline-flex items-start gap-2"><X className="mt-0.5 h-4 w-4 shrink-0 text-ink/30" /> {b}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-surface" data-testid="country-dedicated">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <p className="kicker">{DEDICATED[c.lang][0]}</p>
          <h2 className={H2}>{DEDICATED[c.lang][1]}</h2>
          <div className="mt-12 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-3">
            {marketFor(c.lang).map((m) => (
              <Link key={m.slug} to={`/${m.slug}`} data-testid={`country-dedicated-${m.slug}`} className="group flex items-start justify-between gap-4 bg-base p-6 transition-colors hover:bg-surface">
                <div><p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/45">{m.kicker}</p><h3 className="mt-2 font-heading text-lg font-semibold leading-snug text-ink">{m.title}</h3></div>
                <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-ink/40 transition-colors group-hover:text-gold" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <p className="kicker">{c.process_k}</p>
          <h2 className={H2}>{c.process_t}</h2>
          <div className="mt-12 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="country-steps">
            {c.steps.map(([title, text], i) => (
              <div key={title} className="bg-base p-8">
                <span className="font-mono text-[12px] tracking-[0.25em] text-ink/40">0{i + 1}</span>
                <h3 className="mt-6 text-[16px] font-bold tracking-tight text-ink">{title}</h3>
                <p className="mt-3 text-[15px] leading-[1.6] text-ink/65">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-surface">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="kicker">{c.faq_k}</p>
              <h2 className={H2}>{c.faq_t}</h2>
            </div>
            <div className="border-t border-ink/10 lg:col-span-8" data-testid="country-faq">
              {c.faq.map(([q, a], i) => (
                <details key={q} className="group border-b border-ink/10" data-testid={`country-faq-${i}`}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-[17px] font-semibold text-ink [&::-webkit-details-marker]:hidden">
                    {q}
                    <span className="shrink-0 font-mono text-xl text-ink/40 transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="pb-6 text-[15px] leading-[1.7] text-ink/65">{a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto grid max-w-7xl gap-16 px-4 py-24 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="kicker">{c.kicker}</p>
            <h2 className={`${H2} leading-[1.1]`}>{c.cta_t}</h2>
            <p className="mt-6 max-w-md text-[15px] leading-[1.7] text-ink/65">{c.cta_p}</p>
          </div>
          <Reveal delay={0.1}><ContactForm defaultSubject={c.form_subject} /></Reveal>
        </div>
      </section>
    </div>
  );
}
